// ============================================================
// Military Constants — Single source of truth
// Branch/rank data for all registration forms, settings, admin
// ============================================================

export type ServiceBranchEntry = {
  value: string;
  label: string;
};

/** 15 service branches in display order per issue #811 */
export const SERVICE_BRANCHES: ServiceBranchEntry[] = [
  { value: 'USN', label: 'US Navy' },
  { value: 'USMC', label: 'US Marine Corps' },
  { value: 'USA', label: 'US Army' },
  { value: 'USAF', label: 'US Air Force' },
  { value: 'USSF', label: 'US Space Force' },
  { value: 'USCG', label: 'US Coast Guard' },
  { value: 'USNR', label: 'US Navy Reserve' },
  { value: 'USN Reserve', label: 'USN Reserve' },
  { value: 'USMC Reserve', label: 'USMC Reserve' },
  { value: 'USA Reserve', label: 'USA Reserve' },
  { value: 'ARNG', label: 'Army National Guard' },
  { value: 'USAF Reserve', label: 'USAF Reserve' },
  { value: 'ANG', label: 'Air National Guard' },
  { value: 'USCG Reserve', label: 'USCG Reserve' },
  { value: 'International Military', label: 'International Military' },
];

export const ALL_SERVICE_BRANCH_VALUES = SERVICE_BRANCHES.map((b) => b.value);

export const CIVILIAN_TITLES = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'] as const;

// ============================================================
// Rank groups by service branch
// ============================================================

type RankGroup = { category: string; ranks: string[] };

const USN_RANKS: RankGroup[] = [
  { category: 'Enlisted', ranks: ['SR', 'SA', 'SN', 'PO3', 'PO2', 'PO1', 'CPO', 'SCPO', 'MCPO', 'MCPON'] },
  { category: 'Warrant', ranks: ['WO1', 'CWO2', 'CWO3', 'CWO4', 'CWO5'] },
  { category: 'Officer', ranks: ['ENS', 'LTJG', 'LT', 'LCDR', 'CDR', 'CAPT', 'RDML', 'RADM', 'VADM', 'ADM'] },
];

const USMC_RANKS: RankGroup[] = [
  { category: 'Enlisted', ranks: ['Pvt', 'PFC', 'LCpl', 'Cpl', 'Sgt', 'SSgt', 'GySgt', 'MSgt', '1stSgt', 'MGySgt', 'SgtMaj', 'SMMC'] },
  { category: 'Warrant', ranks: ['WO1', 'CWO2', 'CWO3', 'CWO4', 'CWO5'] },
  { category: 'Officer', ranks: ['2ndLt', '1stLt', 'Capt', 'Maj', 'LtCol', 'Col', 'BGen', 'MajGen', 'LtGen', 'Gen'] },
];

const USA_RANKS: RankGroup[] = [
  { category: 'Enlisted', ranks: ['PV1', 'PV2', 'PFC', 'SPC', 'CPL', 'SGT', 'SSG', 'SFC', 'MSG', '1SG', 'SGM', 'CSM', 'SMA'] },
  { category: 'Warrant', ranks: ['WO1', 'CW2', 'CW3', 'CW4', 'CW5'] },
  { category: 'Officer', ranks: ['2LT', '1LT', 'CPT', 'MAJ', 'LTC', 'COL', 'BG', 'MG', 'LTG', 'GEN'] },
];

const USAF_RANKS: RankGroup[] = [
  { category: 'Enlisted', ranks: ['AB', 'Amn', 'A1C', 'SrA', 'SSgt', 'TSgt', 'MSgt', 'SMSgt', 'CMSgt', 'CCM', 'CMSAF'] },
  { category: 'Officer', ranks: ['2d Lt', '1st Lt', 'Capt', 'Maj', 'Lt Col', 'Col', 'Brig Gen', 'Maj Gen', 'Lt Gen', 'Gen'] },
];

const USSF_RANKS: RankGroup[] = [
  { category: 'Enlisted', ranks: ['Spc1', 'Spc2', 'Spc3', 'Spc4', 'Sgt', 'TSgt', 'MSgt', 'SMSgt', 'CMSgt', 'CMSSF'] },
  { category: 'Officer', ranks: ['2d Lt', '1st Lt', 'Capt', 'Maj', 'Lt Col', 'Col', 'Brig Gen', 'Maj Gen', 'Lt Gen', 'Gen'] },
];

const USCG_RANKS: RankGroup[] = [
  { category: 'Enlisted', ranks: ['SR', 'SA', 'SN', 'PO3', 'PO2', 'PO1', 'CPO', 'SCPO', 'MCPO', 'MCPOCG'] },
  { category: 'Warrant', ranks: ['CWO2', 'CWO3', 'CWO4'] },
  { category: 'Officer', ranks: ['ENS', 'LTJG', 'LT', 'LCDR', 'CDR', 'CAPT', 'RDML', 'RADM', 'VADM', 'ADM'] },
];

/** Map from branch value to its rank groups. Shared branches point to the same array. */
export const RANKS_BY_BRANCH: Record<string, RankGroup[]> = {
  'USN': USN_RANKS,
  'USNR': USN_RANKS,
  'USN Reserve': USN_RANKS,
  'USMC': USMC_RANKS,
  'USMC Reserve': USMC_RANKS,
  'USA': USA_RANKS,
  'USA Reserve': USA_RANKS,
  'ARNG': USA_RANKS,
  'USAF': USAF_RANKS,
  'USAF Reserve': USAF_RANKS,
  'ANG': USAF_RANKS,
  'USSF': USSF_RANKS,
  'USCG': USCG_RANKS,
  'USCG Reserve': USCG_RANKS,
};

/**
 * Get the available ranks for a given branch.
 * Returns branch-specific ranks + civilian titles, or just civilian titles
 * when no branch is selected or branch is "International Military".
 */
export function getRanksForBranch(branch: string | null | undefined): string[] {
  if (!branch || branch === 'International Military') {
    return [...CIVILIAN_TITLES];
  }
  const groups = RANKS_BY_BRANCH[branch];
  if (!groups) return [...CIVILIAN_TITLES];
  const branchRanks = groups.flatMap((g) => g.ranks);
  return [...branchRanks, ...CIVILIAN_TITLES];
}

/**
 * Get rank groups with categories for grouped dropdown display.
 * Returns an array of { category, ranks } for the selected branch,
 * plus a "Civilian" group with civilian titles.
 */
export function getRankGroupsForBranch(branch: string | null | undefined): RankGroup[] {
  const civilianGroup: RankGroup = { category: 'Civilian', ranks: [...CIVILIAN_TITLES] };
  if (!branch || branch === 'International Military') {
    return [civilianGroup];
  }
  const groups = RANKS_BY_BRANCH[branch];
  if (!groups) return [civilianGroup];
  return [...groups, civilianGroup];
}

/** Flat array of every valid rank value (for backend validation) */
export const ALL_VALID_RANKS: string[] = Array.from(
  new Set([
    ...Object.values(RANKS_BY_BRANCH).flatMap((groups) =>
      groups.flatMap((g) => g.ranks)
    ),
    ...CIVILIAN_TITLES,
  ])
);
